import React, { useEffect, useMemo, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard, Panel, EmptyState } from "@/components/ui-parts";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { ArrowUpRight } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([api.get("/reports/summary"), api.get("/units")]);
        setSummary(s.data);
        setUnits(u.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byUnitData = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.by_unit || {}).map(([k, v]) => ({
      unit: UNIT_META[k]?.tag || k.toUpperCase(),
      output: v.output_kg || 0,
      wastage: v.wastage_kg || 0,
      color: UNIT_META[k]?.accent || "#FFB224",
    }));
  }, [summary]);

  if (loading) {
    return <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Loading dashboard…</div>;
  }

  const t = summary?.totals || {};

  return (
    <div>
      <PageHeader
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0] || "Chief"}`}
        title="Operational Overview"
        description="A live snapshot across all your verticals — production output, electricity draw, wastage and open sauda contracts."
        testid="dashboard-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="kpi-row">
        <KpiCard testid="kpi-output" label="Output (kg)" tone="amber" value={fmt(t.production_output_kg)} sub="cumulative" />
        <KpiCard testid="kpi-yield" label="Yield %" tone="success" value={`${t.yield_pct ?? 0}%`} sub="output / input" />
        <KpiCard testid="kpi-electricity" label="Electricity (kWh)" value={fmt(t.electricity_units)} sub={`₹${fmt(t.electricity_cost)} cost`} />
        <KpiCard testid="kpi-sauda" label="Open Sauda" tone="amber" value={t.sauda_open ?? 0} sub={`${t.sauda_total ?? 0} total`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Panel title="Daily Production (kg)" className="lg:col-span-2">
          <div className="h-72 p-4">
            {summary?.daily_series?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.daily_series}>
                  <CartesianGrid stroke="#27272a" strokeDasharray="2 4" />
                  <XAxis dataKey="date" stroke="#71717a" fontSize={10} />
                  <YAxis stroke="#71717a" fontSize={10} />
                  <Tooltip contentStyle={{ background: "#0e0e11", border: "1px solid #27272a", borderRadius: 0, fontSize: 12 }} />
                  <Line type="monotone" dataKey="output_kg" stroke="#FFB224" strokeWidth={2} dot={{ fill: "#FFB224", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState message="No production yet" />
            )}
          </div>
        </Panel>

        <Panel title="By Unit — Output vs Wastage">
          <div className="h-72 p-4">
            {byUnitData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byUnitData}>
                  <CartesianGrid stroke="#27272a" strokeDasharray="2 4" />
                  <XAxis dataKey="unit" stroke="#71717a" fontSize={10} />
                  <YAxis stroke="#71717a" fontSize={10} />
                  <Tooltip contentStyle={{ background: "#0e0e11", border: "1px solid #27272a", borderRadius: 0, fontSize: 12 }} />
                  <Bar dataKey="output" name="Output">
                    {byUnitData.map((d) => (
                      <Cell key={d.unit} fill={d.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState message="No unit data yet" />
            )}
          </div>
        </Panel>
      </div>

      <div>
        <div className="label-caps mb-3">Business Units</div>
        <div className="grid grid-scaffold grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {units.map((u) => {
            const meta = UNIT_META[u.key] || {};
            const stats = summary?.by_unit?.[u.key] || {};
            return (
              <div key={u.key} className="p-5 bg-[#0e0e11] flex flex-col justify-between min-h-[180px] group hover:bg-zinc-900 transition-colors" data-testid={`unit-card-${u.key}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div
                      className="label-caps text-[10px] mb-2"
                      style={{ color: meta.accent }}
                    >
                      {meta.tag}
                    </div>
                    <div className="font-display font-black text-lg leading-tight tracking-tight">
                      {u.name}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                      {u.description}
                    </div>
                  </div>
                  <ArrowUpRight size={16} className="text-zinc-600 group-hover:text-amber-400" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-zinc-800 text-[11px] font-mono">
                  <div>
                    <div className="label-caps text-[9px]">Output</div>
                    <div className="text-zinc-100 font-semibold">{fmt(stats.output_kg)} kg</div>
                  </div>
                  <div>
                    <div className="label-caps text-[9px]">Wastage</div>
                    <div className="text-zinc-100 font-semibold">{fmt(stats.wastage_kg)} kg</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function fmt(n) {
  if (n === undefined || n === null) return "0";
  return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 1 });
}
